import React from "react";
import { Link } from "react-router-dom";

import { decodeMimeEncodedWords } from "../models/inboxConversationListViewModel";
import "./BusinessObjectCard.css";

const ENTITY_TYPE_LANGUAGE = {
    flight: "航班",
    flight_operation: "航班运行",
    flight_cargo_load: "货邮装载",
    schedule: "航班计划",
    flight_schedule: "航班计划",
    catering_order: "配餐订单",
    catering_service: "配餐服务",
    fuel_request: "燃油申请",
    permit_request: "许可申请",
    crew_transport: "机组接送",
    crew_transportation: "机组接送",
    hotel_booking: "酒店住宿",
    invoice: "账单",
    settlement_invoice: "结算账单",
    ap_invoice: "应付账单",
    order: "订单",
    supplier: "供应商",
    customer: "客户",
    document: "文档",
};

const SUMMARY_FIELD_PRIORITIES = {
    flight_operation: [
        "flight_number",
        "flight_no",
        "flight_date",
        "departure_airport",
        "arrival_airport",
        "status",
        "aircraft_registration",
        "std",
        "etd",
        "atd",
        "sta",
        "eta",
        "ata",
    ],
    flight: [
        "flight_number",
        "flight_no",
        "flight_date",
        "departure_airport",
        "arrival_airport",
        "status",
    ],
    catering_order: [
        "order_number",
        "flight_number",
        "flight_date",
        "departure_airport",
        "arrival_airport",
        "supplier_name",
        "service_type",
        "status",
    ],
    fuel_request: [
        "request_number",
        "flight_number",
        "flight_date",
        "supplier_name",
        "fuel_quantity",
        "status",
    ],
    permit_request: [
        "request_number",
        "flight_number",
        "flight_date",
        "permit_type",
        "status",
    ],
    flight_cargo_load: [
        "flight_number",
        "flight_date",
        "departure_airport",
        "arrival_airport",
        "awb_number",
        "uld_number",
        "status",
    ],
};

const DEFAULT_SUMMARY_FIELDS = [
    "flight_number",
    "order_number",
    "request_number",
    "invoice_number",
    "reference_no",
    "name",
    "title",
    "flight_date",
    "departure_airport",
    "arrival_airport",
    "status",
];

const DISPLAY_NAME_FIELDS = [
    "display_name",
    "displayName",
    "entity_display_name",
    "label",
    "title",
    "name",
    "flight_number",
    "flight_no",
    "order_number",
    "request_number",
    "invoice_number",
    "reference_no",
];

const FIELD_LANGUAGE = {
    flight_number: "航班号",
    flight_no: "航班号",
    departure_airport: "始发",
    arrival_airport: "到达",
    flight_date: "航班日期",
    status: "状态",
    aircraft_registration: "机号",
    std: "计划起飞",
    etd: "预计起飞",
    atd: "实际起飞",
    sta: "计划到达",
    eta: "预计到达",
    ata: "实际到达",
    order_number: "订单号",
    request_number: "申请号",
    invoice_number: "账单号",
    reference_no: "参考号",
    supplier_name: "供应商",
    service_type: "服务类型",
    fuel_quantity: "燃油量",
    permit_type: "许可类型",
    awb_number: "运单号",
    uld_number: "ULD",
    name: "名称",
    title: "标题",
};

const STATUS_LANGUAGE = {
    confirmed: { label: "已确认", tone: "success" },
    active: { label: "已确认", tone: "success" },
    applied: { label: "已确认", tone: "success" },
    approved: { label: "已确认", tone: "success" },
    accepted: { label: "已确认", tone: "success" },
    linked: { label: "已确认", tone: "success" },
    verified: { label: "已确认", tone: "success" },
    candidate: { label: "待确认", tone: "warning" },
    pending: { label: "待确认", tone: "warning" },
    pending_review: { label: "待审核", tone: "warning" },
    review: { label: "待审核", tone: "warning" },
    rejected: { label: "已排除", tone: "neutral" },
    dismissed: { label: "已排除", tone: "neutral" },
    closed: { label: "已关闭", tone: "neutral" },
    unknown: { label: "状态待核对", tone: "neutral" },
    conflict: { label: "关联冲突", tone: "danger" },
    conflicted: { label: "关联冲突", tone: "danger" },
};

const LINK_KIND_LANGUAGE = {
    factual: "事实关联",
    fact: "事实关联",
    handling: "处置关联",
    action: "处置关联",
};

const SOURCE_METHOD_LANGUAGE = {
    rule: "规则识别",
    agent: "AG 判断",
    manual: "人工确认",
    business_callback: "业务回写",
};

const LINK_TYPE_LANGUAGE = {
    mvt_parse: "报文解析",
    cpm_parse: "报文解析",
    ffm_parse: "报文解析",
    fwb_parse: "报文解析",
    agent_judgement: "AG 判断",
    agent_judgment: "AG 判断",
    manual_write: "人工确认",
    source_inherited: "来源继承",
};

function toArray(value) {
    return Array.isArray(value) ? value : [];
}

function toRecord(value) {
    return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function normalizeWhitespace(value) {
    return String(value).replace(/\s+/g, " ").trim();
}

function normalizeText(value, fallback = "") {
    if (value === null || value === undefined) return fallback;
    if (typeof value === "object") return fallback;
    const decoded = normalizeWhitespace(decodeMimeEncodedWords(String(value)));
    return decoded || fallback;
}

function normalizeSnapshotValue(value) {
    if (Array.isArray(value)) {
        return value.map(normalizeSnapshotValue).filter(Boolean).join("、");
    }
    return normalizeText(value);
}

function formatShortId(value) {
    const text = normalizeText(value);
    if (!text) return "";
    return text.length > 12 ? `${text.slice(0, 8)}…${text.slice(-4)}` : text;
}

function normalizeKey(value) {
    return normalizeText(value).toLowerCase();
}

export function getBusinessObjectTypeLabel(entityType) {
    const key = normalizeKey(entityType);
    return ENTITY_TYPE_LANGUAGE[key] || "";
}

function getDisplayName(snapshot, entityId) {
    const record = toRecord(snapshot);
    for (const key of DISPLAY_NAME_FIELDS) {
        const value = normalizeSnapshotValue(record[key]);
        if (value) return value;
    }
    return formatShortId(entityId);
}

function getSummaryFields(entityType, snapshot) {
    const record = toRecord(snapshot);
    const priority = SUMMARY_FIELD_PRIORITIES[normalizeKey(entityType)] || DEFAULT_SUMMARY_FIELDS;
    const used = new Set();

    return priority.reduce((items, key) => {
        if (used.has(key)) return items;
        const value = normalizeSnapshotValue(record[key]);
        if (!value) return items;
        used.add(key);
        items.push({ key, label: FIELD_LANGUAGE[key] || key, value });
        return items;
    }, []);
}

function getRoutePath(snapshot) {
    const routePath = normalizeText(toRecord(snapshot).route_path);
    if (!routePath.startsWith("/") || routePath.startsWith("//")) return "";
    return routePath;
}

function mapStatus(status) {
    const key = normalizeKey(status);
    return STATUS_LANGUAGE[key] || null;
}

function getLinkStatus(link) {
    if (link?.status?.label) return link.status;
    return mapStatus(link?.review_status ?? link?.reviewStatus ?? link?.status);
}

function uniqueByLabel(items) {
    const seen = new Set();
    return items.filter((item) => {
        const key = item.label;
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

function buildStatuses(links, fallbackStatus) {
    const linkStatuses = toArray(links).map(getLinkStatus);
    if (linkStatuses.some((item) => !item)) {
        return { error: "业务关联状态契约错误：存在未知 review_status。", items: [] };
    }
    if (linkStatuses.length === 0 && fallbackStatus?.label) {
        linkStatuses.push(fallbackStatus);
    }
    return { error: "", items: uniqueByLabel(linkStatuses).slice(0, 3) };
}

function mapLinkKind(link) {
    const linkKind = normalizeKey(link?.link_kind ?? link?.linkKind);
    if (LINK_KIND_LANGUAGE[linkKind]) return LINK_KIND_LANGUAGE[linkKind];
    return "";
}

function mapSourceMethod(link) {
    const sourceMethod = normalizeKey(link?.source_method ?? link?.sourceMethod);
    if (SOURCE_METHOD_LANGUAGE[sourceMethod]) return SOURCE_METHOD_LANGUAGE[sourceMethod];

    const linkType = normalizeKey(link?.link_type ?? link?.linkType);
    return LINK_TYPE_LANGUAGE[linkType] || "";
}

function buildLinkSummaries(links) {
    return uniqueByLabel(toArray(links).map((link, index) => {
        const kind = normalizeText(link?.linkKindLabel) || mapLinkKind(link);
        const source = normalizeText(link?.sourceMethodLabel) || mapSourceMethod(link);
        const description = normalizeText(link?.description);
        const label = [kind, source].filter(Boolean).join(" · ");

        return {
            key: normalizeText(link?.id, String(index)),
            label,
            description,
        };
    }).filter((item) => item.label)).slice(0, 3);
}

function buildCompactText(typeLabel, displayName, entityId, summaryFields) {
    const values = [typeLabel, displayName || formatShortId(entityId), ...summaryFields.slice(0, 2).map((item) => item.value)];
    const seen = new Set();
    return values.filter((value) => {
        if (!value || seen.has(value)) return false;
        seen.add(value);
        return true;
    }).join(" · ");
}

export default function BusinessObjectCard({
    entityType = "",
    entityId = "",
    snapshot = null,
    links = [],
    status = null,
    compact = false,
}) {
    const typeLabel = getBusinessObjectTypeLabel(entityType);
    const displayName = getDisplayName(snapshot, entityId);
    const summaryFields = getSummaryFields(entityType, snapshot);
    const statusResult = buildStatuses(links, status);
    const statuses = statusResult.items;
    const linkSummaries = buildLinkSummaries(links);
    const routePath = getRoutePath(snapshot);
    const identity = displayName || formatShortId(entityId) || "未标明对象";

    if (!typeLabel || statusResult.error) {
        const errorText = !typeLabel
            ? `业务对象类型契约错误：未知 entity_type ${entityType || "<empty>"}。`
            : statusResult.error;
        if (compact) {
            return (
                <span className="business-object-card business-object-card-compact" title={errorText}>
                    <span className="business-object-card-compact-text">业务对象契约错误</span>
                </span>
            );
        }

        return (
            <article className="business-object-card">
                <div className="business-object-card-header">
                    <div className="business-object-card-title-block">
                        <span className="business-object-card-type">契约错误</span>
                        <strong className="business-object-card-title">{errorText}</strong>
                    </div>
                </div>
            </article>
        );
    }

    if (compact) {
        return (
            <span className="business-object-card business-object-card-compact" title={buildCompactText(typeLabel, displayName, entityId, summaryFields)}>
                <span className="business-object-card-compact-text">
                    {buildCompactText(typeLabel, displayName, entityId, summaryFields)}
                </span>
                {statuses[0] ? (
                    <span className={`business-object-card-status business-object-card-status-${statuses[0].tone || "neutral"}`}>
                        {statuses[0].label}
                    </span>
                ) : null}
            </span>
        );
    }

    return (
        <article className="business-object-card">
            <div className="business-object-card-header">
                <div className="business-object-card-title-block">
                    <span className="business-object-card-type">{typeLabel}</span>
                    <strong className="business-object-card-title">{identity}</strong>
                    {entityId ? <span className="business-object-card-id">ID {formatShortId(entityId)}</span> : null}
                </div>
                {statuses.length > 0 ? (
                    <div className="business-object-card-status-row" aria-label="关联状态">
                        {statuses.map((item) => (
                            <span key={item.label} className={`business-object-card-status business-object-card-status-${item.tone || "neutral"}`}>
                                {item.label}
                            </span>
                        ))}
                    </div>
                ) : null}
            </div>

            {summaryFields.length > 0 ? (
                <dl className="business-object-card-summary">
                    {summaryFields.slice(0, 5).map((item) => (
                        <div key={item.key} className="business-object-card-summary-item">
                            <dt>{item.label}</dt>
                            <dd>{item.value}</dd>
                        </div>
                    ))}
                </dl>
            ) : (
                <p className="business-object-card-fallback">当前仅有业务对象类型和 ID。</p>
            )}

            {linkSummaries.length > 0 ? (
                <div className="business-object-card-link-list" aria-label="关联方式">
                    {linkSummaries.map((item) => (
                        <span key={item.key} className="business-object-card-link-chip" title={item.description || item.label}>
                            {item.label}
                        </span>
                    ))}
                </div>
            ) : null}

            {routePath ? (
                <Link className="business-object-card-detail-link" to={routePath}>
                    查看详情
                </Link>
            ) : null}
        </article>
    );
}
